// src/pages/Blog.js
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import ArticleCard from '../components/ArticleCard';
import { fetchBlogPosts } from '../services/apiService';
import FinalCTA from '../components/FinalCTA';
import PricingPreview from '../components/PricingPreview';
import BlogShowcase from '../components/BlogShowcase';

const Blog = () => {
  // ✅ FIX: Renamed state to 'allPosts' for clarity, as it's an array.
  const [allPosts, setAllPosts] = useState([]); 
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const getPosts = async () => {
      try {
        // ✅ FIX: Destructure the 'posts' array from the response object.
        // ✅ OPTIMIZATION: Fetch only the 7 posts needed for this page.
        const { posts: postsData } = await fetchBlogPosts({ page: 1, perPage: 7 });
        
        // ✅ FIX: Set the state with the array, not the parent object.
        setAllPosts(postsData); 
      } catch (err) {
        setError('Failed to load posts.');
        console.error(err);
      }
      setLoading(false);
    };
    getPosts();
  }, []);

  // ✅ FIX: Ensure allPosts is an array and has items before slicing.
  const latestPost = allPosts?.[0];
  const otherPosts = allPosts?.slice(1, 7) || [];

  return (
    <>
      <SEO
        title="Oolook Logbook | AI & Social Media Marketing Blog"
        description="Stay ahead with expert insights from the Oolook team. Explore articles on AI marketing, social media strategy, content creation, and productivity."
      />
      <main className="bg-dark-bg">
        <div className="pt-24 sm:pt-32">
          <div className="mx-auto max-w-7xl px-6 lg:px-8">
            <div className="mx-auto max-w-2xl text-center">
              <h1 className="text-3xl font-bold tracking-tight text-dark-text-primary sm:text-4xl">From the Logbook</h1>
              <p className="mt-2 text-lg leading-8 text-dark-text-secondary">
                Expert insights on AI, marketing, and building a brand that lasts.
              </p>
            </div>

            {loading && <p className="text-center mt-16">Loading latest posts...</p>}
            {error && <p className="text-center mt-16 text-red-400">{error}</p>}

            {/* Featured Post */}
            {latestPost && (
              <div className="mt-16 grid grid-cols-1 items-center gap-12 border-b border-white/10 pb-16 lg:grid-cols-2">
                <Link to={`/blog/${latestPost.slug}`} className="group block aspect-square rounded-2xl overflow-hidden">
                   <img src={latestPost.featuredMedia?.source_url} alt={latestPost.title} className="w-full h-full object-cover transition-transform duration-500 ease-in-out group-hover:scale-105" />
                </Link>
                <div>
                   <p className="text-base font-semibold text-dark-accent-start">
                        {latestPost.categories?.[0]?.name || 'Featured'}
                    </p>
                    <h2 className="mt-4 text-3xl font-bold text-dark-text-primary">
                        <Link to={`/blog/${latestPost.slug}`} dangerouslySetInnerHTML={{ __html: latestPost.title }}/>
                    </h2>
                    {/* ✅ FIX: The excerpt is now an HTML string, not an object */}
                    <p className="mt-4 text-dark-text-secondary" dangerouslySetInnerHTML={{ __html: latestPost.excerpt }} />
                    <div className="mt-6 flex items-center gap-x-4">
                        {latestPost.author.avatarUrl && <img src={latestPost.author.avatarUrl} alt="" className="h-10 w-10 rounded-full bg-gray-800" />}
                        <div className="text-sm leading-6">
                            <p className="font-semibold text-dark-text-primary">{latestPost.author.name}</p>
                            <p className="text-dark-text-secondary/70">{new Date(latestPost.dateTime).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</p>
                        </div>
                    </div>
                </div>
              </div>
            )}

            {/* Grid of other posts */}
            <div className="mx-auto mt-16 grid max-w-2xl grid-cols-1 gap-8 sm:mt-20 lg:mx-0 lg:max-w-none lg:grid-cols-3">
              {otherPosts.map(post => <ArticleCard key={post.id} post={post} />)}
            </div>

            <div className="text-center mt-16">
                <Link to="/all-articles" className="text-sm font-semibold text-dark-text-primary hover:text-white">
                    View all posts &rarr;
                </Link>
            </div>
          </div>
        </div>
        
        <BlogShowcase 
            title="Latest from the Logbook"
            subtitle="Insights on AI, social media strategy, and building a brand that lasts."
          />
        <PricingPreview />
        <FinalCTA />
      </main>
    </>
  );
};

export default Blog;